import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Download, Copy, Image, Clock, CheckCircle, AlertCircle, Loader, RefreshCw, Settings, CheckCircle2 } from 'lucide-react'
import { buttonVariants, componentClasses, cn } from '../../utils/designSystem'
import { getAvailableAspectRatios, downloadImage } from './gambarUtils'
import AgentHeader from '../../components/AgentHeader'

const GambarResultPage = ({ 
  query, 
  generatedImage, 
  isLoading, 
  error, 
  isCompleted, 
  aspectRatio,
  onBack, 
  onRegenerateImage
}) => {
  const [startTime] = useState(Date.now())
  const [copyStatus, setCopyStatus] = useState('idle')
  const [showSettings, setShowSettings] = useState(false)
  const [selectedAspectRatio, setSelectedAspectRatio] = useState(aspectRatio)

  // Auto scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const availableAspectRatios = getAvailableAspectRatios()

  // Copy image URL to clipboard
  const handleCopyImageUrl = async () => {
    if (!generatedImage?.supabaseUrl) return
    
    try {
      // Gunakan URL Supabase yang sudah tersimpan
      await navigator.clipboard.writeText(generatedImage.supabaseUrl)
      setCopyStatus('success')
      setTimeout(() => setCopyStatus('idle'), 2000)
    } catch (err) {
      setCopyStatus('error')
      setTimeout(() => setCopyStatus('idle'), 2000)
    }
  }

  // Download image
  const handleDownloadImage = async () => {
    if (!generatedImage?.supabaseUrl) return
    
    try {
      const filename = `gambar-ai-${Date.now()}`
      // Gunakan URL Supabase yang sudah tersimpan
      await downloadImage(generatedImage.supabaseUrl, filename)
    } catch (err) {
      console.error('Error downloading image:', err)
    }
  }

  // Handle aspect ratio change
  const handleAspectRatioChange = (newAspectRatio) => {
    setSelectedAspectRatio(newAspectRatio)
    setShowSettings(false)
    onRegenerateImage(newAspectRatio)
  }

  // Format generation time
  const formatGenerationTime = () => {
    if (!generatedImage?.generatedAt) return null
    const generatedTime = new Date(generatedImage.generatedAt)
    const duration = Math.round((generatedTime - startTime) / 1000)
    return duration > 0 ? `${duration} detik` : 'Kurang dari 1 detik'
  }

  return (
    <div className="min-h-screen bg-bg-primary overflow-x-hidden">
      {/* Header */}
      <AgentHeader
        onBack={onBack}
        icon={Image}
        title="Gambar Agent"
        query="Generator Gambar AI"
        iconColor="text-white"
        iconBgColor="bg-gradient-to-br from-purple-500 to-pink-500"
        actions={isCompleted && generatedImage ? [
          {
            onClick: () => setShowSettings(!showSettings),
            icon: Settings,
            label: 'Pengaturan',
            hideOnMobile: true,
            className: cn(buttonVariants.secondary, "flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm")
          },
          {
            onClick: handleCopyImageUrl,
            icon: Copy,
            label: copyStatus === 'success' ? 'Tersalin!' : copyStatus === 'error' ? 'Gagal' : 'Salin URL',
            hideOnMobile: true,
            className: cn(
              buttonVariants.secondary,
              "flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm",
              copyStatus === 'success' && "bg-green-500/20 text-green-400",
              copyStatus === 'error' && "bg-red-500/20 text-red-400"
            )
          },
          {
            onClick: handleDownloadImage,
            icon: Download,
            label: 'Unduh',
            hideOnMobile: true,
            className: cn(buttonVariants.primary, "flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm")
          }
        ] : []}
      />

      {/* Settings Panel */}
      {showSettings && (
        <div className="max-w-6xl mx-auto px-2 sm:px-4 py-3 sm:py-4">
          <div className="bg-bg-secondary rounded-lg p-4 border border-border-secondary">
            <h3 className="text-xs sm:text-sm font-medium text-text-primary mb-2 sm:mb-3">Aspect Ratio</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
              {availableAspectRatios.map((ratio) => (
                <button
                  key={ratio.value}
                  onClick={() => handleAspectRatioChange(ratio.value)}
                  className={cn(
                    "p-3 rounded-lg border text-left transition-colors",
                    selectedAspectRatio === ratio.value
                      ? "border-blue-500 bg-blue-500/10 text-blue-400"
                      : "border-border-secondary bg-bg-tertiary text-text-secondary hover:border-border-primary"
                  )}
                >
                  <div className="font-medium text-sm">{ratio.label}</div>
                  <div className="text-xs text-text-tertiary">{ratio.description}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-2 sm:px-4 py-3 sm:py-6">
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-3 sm:gap-6">
          {/* Left Column - Image Display */}
          <div className="xl:col-span-2">
            <div className="bg-bg-secondary rounded-lg border border-border-secondary overflow-hidden">
              {/* Query Display */}
              <div className="p-3 sm:p-4 border-b border-border-secondary">
                <h2 className="text-base sm:text-lg font-semibold text-text-primary mb-2">Prompt</h2>
                <p className="text-sm sm:text-base text-text-secondary leading-relaxed break-words">{query}</p>
              </div>

              {/* Image Content */}
              <div className="p-3 sm:p-4">
                {isLoading && (
                  <div className="flex flex-col items-center justify-center py-12 space-y-4">
                    <div className="relative">
                      <Loader className="w-8 h-8 text-blue-500 animate-spin" />
                    </div>
                    <div className="text-center">
                      <p className="text-text-primary font-medium">Membuat gambar...</p>
                      <p className="text-text-tertiary text-sm mt-1">Mohon tunggu sebentar</p>
                    </div>
                  </div>
                )}

                {error && (
                  <div className="flex flex-col items-center justify-center py-12 space-y-4">
                    <AlertCircle className="w-8 h-8 text-red-500" />
                    <div className="text-center">
                      <p className="text-red-400 font-medium">Terjadi Kesalahan</p>
                      <p className="text-text-tertiary text-sm mt-1">{error}</p>
                      <button
                        onClick={() => onRegenerateImage(aspectRatio)}
                        className={cn(
                          buttonVariants.primary,
                          "flex items-center space-x-2 px-4 py-2 mt-4"
                        )}
                      >
                        <RefreshCw className="w-4 h-4" />
                        <span>Coba Lagi</span>
                      </button>
                    </div>
                  </div>
                )}

                {isCompleted && generatedImage && (
                  <div className="space-y-4">
                    <div className="relative group">
                      <img
                        src={generatedImage.supabaseUrl}
                        alt={query}
                        className="w-full h-auto rounded-lg shadow-lg"
                        style={{
                          maxHeight: '70vh',
                          objectFit: 'contain'
                        }}
                        onError={(e) => {
                          console.error('Error loading image from Supabase:', e)
                        }}
                      />
                      
                      {/* Image Overlay Actions */}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center space-x-4">
                        <button
                          onClick={handleDownloadImage}
                          className="bg-white/20 backdrop-blur-sm text-white p-3 rounded-full hover:bg-white/30 transition-colors"
                          title="Download gambar"
                        >
                          <Download className="w-5 h-5" />
                        </button>
                        <button
                          onClick={handleCopyImageUrl}
                          className="bg-white/20 backdrop-blur-sm text-white p-3 rounded-full hover:bg-white/30 transition-colors"
                          title="Salin URL gambar"
                        >
                          <Copy className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                    
                    {/* Regenerate Button */}
                    <div className="flex justify-center">
                      <button
                        onClick={() => onRegenerateImage(aspectRatio)}
                        className={cn(
                          buttonVariants.secondary,
                          "flex items-center space-x-2 px-4 py-2"
                        )}
                      >
                        <RefreshCw className="w-4 h-4" />
                        <span>Buat Ulang Gambar</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Info Panel */}
          <div className="space-y-6">
            {/* Status Card */}
            <div className={cn(componentClasses.card, "p-4")}>
              <div className="flex items-center space-x-3 mb-4">
                {isLoading && <Loader className="w-5 h-5 text-blue-500 animate-spin" />}
                {error && <AlertCircle className="w-5 h-5 text-red-500" />}
                {isCompleted && <CheckCircle className="w-5 h-5 text-green-500" />}
                <h3 className="font-semibold text-text-primary">
                  {isLoading ? 'Sedang Memproses...' :
                   error ? 'Gagal' :
                   isCompleted ? 'Selesai' : 'Menunggu'}
                </h3>
              </div>
              
              {isCompleted && generatedImage && (
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex flex-col">
                    <span className="text-text-tertiary text-xs mb-1">Waktu Generasi</span>
                    <span className="text-text-secondary font-medium">{formatGenerationTime()}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-text-tertiary text-xs mb-1">Aspect Ratio</span>
                    <span className="text-text-secondary font-medium">{generatedImage.aspectRatio}</span>
                  </div>
                  <div className="flex flex-col col-span-2">
                    <span className="text-text-tertiary text-xs mb-1">Storage</span>
                    <span className="text-text-secondary font-medium">
                      <span className="text-green-400">✓ Tersimpan di Cara Storage</span>
                    </span>
                  </div>
                  {generatedImage.seed && (
                    <div className="flex flex-col col-span-2">
                      <span className="text-text-tertiary text-xs mb-1">Seed</span>
                      <span className="text-text-secondary font-mono text-xs">{generatedImage.seed}</span>
                    </div>
                  )}
                </div>
              )}
            </div>



            {/* Tips Card */}
            <div className={cn(componentClasses.card, "p-4")}>
              <h3 className="font-semibold text-text-primary mb-3">Tips</h3>
              <ul className="space-y-2 text-sm text-text-secondary">
                <li>• Gunakan deskripsi yang detail untuk hasil terbaik</li>
                <li>• Sebutkan gaya, warna, dan mood yang diinginkan</li>
                <li>• Coba berbagai aspect ratio untuk komposisi yang berbeda</li>
                <li>• Gunakan kata kunci seperti "realistic", "artistic", "cartoon"</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

GambarResultPage.propTypes = {
  query: PropTypes.string.isRequired,
  generatedImage: PropTypes.object,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  isCompleted: PropTypes.bool.isRequired,
  aspectRatio: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  onRegenerateImage: PropTypes.func.isRequired
}

export default GambarResultPage